import { useState, useCallback, useMemo, useEffect } from 'react';
import { Settings, Search, ChevronLeft, ChevronRight, AlertCircle, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useTenantUsers, useAvailableRoles, useUpdateUserRoles } from '@/hooks/useTenantUsers';
import { useUserSettings, useUpdateUserSettings } from '@/hooks/useUserSettings';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { comparatorOptions, type AnalysisDimensionFilter } from '@/utils/filterComparators';
import type { TenantUser, FronteggRole, UserSettings } from '@/services/user.service';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useCustomPeriodVariants } from '@/hooks/useDateDimensionCustomPeriods';
import { useScopeCatalog, useUserGroupMemberships, useUserGroups } from '@/hooks/useUserGroups';
import { apiErrorMessage } from '@/services/api.service';
import type { RowRestriction } from '@/services/userGroup.service';
import { MetricVisibilityEditor, RowRestrictionsEditor, notifyRestrictionChecks } from './security/ScopeEditors';
import PrincipalPermissions from './security/PrincipalPermissions';
import { isAgentUser, useAgentUsers } from './agents/data';

function describeFilter(filter: AnalysisDimensionFilter, dimensionNameById: Record<string, string>): string {
  const comparator = comparatorOptions.find((c) => c.value === filter.comparator)?.label ?? filter.comparator;
  const value = Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '');
  return `${dimensionNameById[filter.dimension_id] ?? filter.dimension_id} ${comparator} ${value}`.trim();
}

/**
 * Filters set on users before row restrictions existed. They follow each metric's
 * strict mode (error or unfiltered) rather than a restriction's rule, so they're not
 * edited in place. Converting one makes it a restriction that hides unconnected
 * metrics — the closest safe equivalent.
 */
function LegacyFilters({
  filters,
  dimensionNameById,
  onRemove,
  onConvertAll,
}: {
  filters: AnalysisDimensionFilter[];
  dimensionNameById: Record<string, string>;
  onRemove: (idx: number) => void;
  onConvertAll: () => void;
}) {
  return (
    <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 space-y-2 mb-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm">
          <div className="font-medium">Older filters</div>
          <div className="text-muted-foreground">
            These were set before row restrictions and still apply. Convert them to edit them here.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onConvertAll}>Convert to restrictions</Button>
      </div>
      {filters.map((f, idx) => (
        <div key={idx} className="flex items-center justify-between text-sm">
          <span>{describeFilter(f, dimensionNameById)}</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onRemove(idx)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function UserPermissionsDialog({
  user,
  open,
  onOpenChange,
}: {
  user: TenantUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: savedSettings, isLoading: settingsLoading } =
    useUserSettings(user.id, { enabled: open });
  // Both branches, independent of Develop mode: a user's scope isn't branched.
  const { data: catalog, isLoading: catalogLoading } = useScopeCatalog({ enabled: open });
  const { data: variants } = useCustomPeriodVariants();
  const { data: availableRoles } = useAvailableRoles();
  const { data: groups } = useUserGroups({ enabled: open });
  const updateSettingsMutation = useUpdateUserSettings();
  const updateRolesMutation = useUpdateUserRoles();

  const [localLegacyFilters, setLocalLegacyFilters] = useState<AnalysisDimensionFilter[] | null>(null);
  const [localRestrictions, setLocalRestrictions] = useState<RowRestriction[] | null>(null);
  const [localBlacklistedIds, setLocalBlacklistedIds] = useState<string[] | null>(null);
  const [localVariant, setLocalVariant] = useState<string | null | undefined>(undefined);
  const [localRoleIds, setLocalRoleIds] = useState<string[] | null>(null);
  const [localGroupId, setLocalGroupId] = useState<string | null | undefined>(undefined);

  const legacyFilters = localLegacyFilters ?? savedSettings?.dimension_filters ?? [];
  const restrictions = localRestrictions ?? savedSettings?.row_restrictions ?? [];
  const blacklistedIds = localBlacklistedIds ?? savedSettings?.blacklisted_metric_ids ?? [];
  const variant = localVariant !== undefined ? localVariant : (savedSettings?.date_dimension_variant ?? null);
  const groupId = localGroupId !== undefined ? localGroupId : (savedSettings?.group_id ?? null);

  const currentRoleIds = useMemo(() => (user.roles || []).map(r => r.id), [user.roles]);
  const selectedRoleIds = localRoleIds ?? currentRoleIds;
  const isBuilderOrAdmin = useMemo(() => {
    const byId = new Map([...(availableRoles ?? []), ...(user.roles ?? [])].map((r) => [r.id, r]));
    return selectedRoleIds.some((id) => ['admin', 'builder'].includes((byId.get(id)?.key ?? '').toLowerCase()));
  }, [availableRoles, user.roles, selectedRoleIds]);
  const selectedGroup = groups?.find((g) => g.id === groupId);
  const hasScope = restrictions.length > 0 || blacklistedIds.length > 0 || legacyFilters.length > 0
    || !!(selectedGroup && (selectedGroup.row_restrictions.length > 0 || selectedGroup.blacklisted_metric_ids.length > 0));

  const resetLocalState = useCallback(() => {
    setLocalLegacyFilters(null);
    setLocalRestrictions(null);
    setLocalBlacklistedIds(null);
    setLocalVariant(undefined);
    setLocalRoleIds(null);
    setLocalGroupId(undefined);
  }, []);

  // Reset local state when settings load
  useEffect(() => {
    if (open) {
      resetLocalState();
    }
  }, [open, resetLocalState]);

  const dimensionNameById = useMemo(
    () => Object.fromEntries((catalog?.dimensions ?? []).map((d) => [d.id, d.name])),
    [catalog]
  );

  const handleConvertLegacy = useCallback(() => {
    setLocalRestrictions([
      ...restrictions,
      ...legacyFilters.map((f): RowRestriction => ({
        dimension_id: f.dimension_id,
        comparator: f.comparator,
        value: f.value as RowRestriction['value'],
        applies_to_metric_ids: [],
        unreachable: 'deny',
      })),
    ]);
    setLocalLegacyFilters([]);
  }, [restrictions, legacyFilters]);

  const handleRoleToggle = useCallback((roleId: string, checked: boolean) => {
    setLocalRoleIds(prev => {
      const current = prev ?? currentRoleIds;
      if (checked) {
        return [...current, roleId];
      } else {
        return current.filter(id => id !== roleId);
      }
    });
  }, [currentRoleIds]);

  const handleSave = useCallback(async () => {
    try {
      const promises: Promise<any>[] = [];
      // Only what was changed in this dialog. Sending everything would write back a
      // possibly stale (or failed-to-load) copy, so saving a role change could wipe
      // the user's group and restrictions, or undo another admin's edit.
      const settings: Partial<UserSettings> = {};
      if (localLegacyFilters !== null) settings.dimension_filters = legacyFilters;
      if (localRestrictions !== null) settings.row_restrictions = restrictions;
      if (localBlacklistedIds !== null) settings.blacklisted_metric_ids = blacklistedIds;
      if (localVariant !== undefined) settings.date_dimension_variant = variant;
      if (localGroupId !== undefined) settings.group_id = groupId;
      const settingsSave = Object.keys(settings).length
        ? updateSettingsMutation.mutateAsync({ userFronteggId: user.id, settings })
        : Promise.resolve(undefined);
      promises.push(settingsSave);
      if (localRoleIds !== null) {
        promises.push(updateRolesMutation.mutateAsync({
          userId: user.id,
          roleIds: localRoleIds,
        }));
      }
      await Promise.all(promises);
      resetLocalState();
      toast.success('User settings saved');
      notifyRestrictionChecks((await settingsSave)?.restriction_checks);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save user settings'));
    }
  }, [legacyFilters, restrictions, blacklistedIds, variant, groupId, localLegacyFilters, localRestrictions, localBlacklistedIds, localVariant, localGroupId, localRoleIds, user.id, updateSettingsMutation, updateRolesMutation, onOpenChange, resetLocalState]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetLocalState();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetLocalState]
  );

  const isSaving = updateSettingsMutation.isPending || updateRolesMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            Settings for {user.name || user.email}
            {user.name && user.email && (
              <span className="text-muted-foreground font-normal text-sm ml-2">{user.email}</span>
            )}
          </DialogTitle>
          <DialogDescription>
            Manage this user's group, their own restrictions and metric visibility, and other settings.
            Their group's restrictions apply on top of their own.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="py-4 overflow-y-auto min-h-0">
          {settingsLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <>
            <div className="flex items-center gap-3 mb-4">
              <Label htmlFor="user-group" className="shrink-0">Group</Label>
              <Select
                value={groupId ?? '__none__'}
                onValueChange={(value) => setLocalGroupId(value === '__none__' ? null : value)}
              >
                <SelectTrigger id="user-group" className="w-[300px]">
                  <SelectValue placeholder="No group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No group</SelectItem>
                  {groups?.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasScope && isBuilderOrAdmin && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm mb-4">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                <p>
                  {user.name || user.email} is an Admin or Builder. Restrictions and hidden metrics apply to their charts,
                  dashboards and Ellie, but those roles can still read raw data and change the data model, so they don't
                  fully limit what this person can see. To restrict someone reliably, give them the ReadOnly role.
                </p>
              </div>
            )}
            <Tabs defaultValue="rows">
              <TabsList>
                <TabsTrigger value="rows">Row Restrictions</TabsTrigger>
                <TabsTrigger value="metrics">Metric Visibility</TabsTrigger>
                <TabsTrigger value="roles">Roles</TabsTrigger>
                <TabsTrigger value="other">Other Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="roles">
                <div className="space-y-3 py-2">
                  <p className="text-sm text-muted-foreground">
                    Select the roles for this user. Changes take effect after saving.
                  </p>
                  {!availableRoles?.length ? (
                    <div className="py-4 text-center text-muted-foreground">Loading roles...</div>
                  ) : (
                    <div className="space-y-2">
                      {availableRoles.map((role) => (
                        <div key={role.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`role-${role.id}`}
                            checked={selectedRoleIds.includes(role.id)}
                            onCheckedChange={(checked) => handleRoleToggle(role.id, !!checked)}
                          />
                          <Label htmlFor={`role-${role.id}`} className="font-normal">
                            {role.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="rows">
                {legacyFilters.length > 0 && (
                  <LegacyFilters
                    filters={legacyFilters}
                    dimensionNameById={dimensionNameById}
                    onRemove={(idx) => setLocalLegacyFilters(legacyFilters.filter((_, i) => i !== idx))}
                    onConvertAll={handleConvertLegacy}
                  />
                )}
                <RowRestrictionsEditor
                  restrictions={restrictions}
                  onChange={setLocalRestrictions}
                  dimensions={catalog?.dimensions ?? []}
                  metrics={catalog?.metrics ?? []}
                  hiddenMetricIds={blacklistedIds}
                />
              </TabsContent>
              <TabsContent value="metrics">
                <MetricVisibilityEditor
                  metrics={catalog?.metrics ?? []}
                  hiddenMetricIds={blacklistedIds}
                  onChange={setLocalBlacklistedIds}
                  isLoading={catalogLoading}
                />
              </TabsContent>
              <TabsContent value="other">
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="variant">Date Dimension Variant</Label>
                    <p className="text-xs text-muted-foreground">
                      When set, this user will see date dimension data from the selected variant.
                    </p>
                    <Select
                      value={variant ?? '__none__'}
                      onValueChange={(value) => setLocalVariant(value === '__none__' ? null : value)}
                    >
                      <SelectTrigger id="variant" className="w-[300px]">
                        <SelectValue placeholder="Default (no variant)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Default (no variant)</SelectItem>
                        {variants?.map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PAGE_SIZE = 20;

export default function UsersTab() {
  const { data: people, isLoading, isError, refetch } = useTenantUsers();
  // Agents are users too, each one for all its steps; listed after people.
  const agents = useAgentUsers();
  const users = useMemo(() => (people ? [...people, ...agents] : people), [people, agents]);
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const handleOpenSettings = useCallback((user: TenantUser) => {
    setSelectedUser(user);
    setDialogOpen(true);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const { data: memberships } = useUserGroupMemberships();
  const { data: groups } = useUserGroups();
  const groupNameById = useMemo(
    () => Object.fromEntries((groups ?? []).map((g) => [g.id, g.name])),
    [groups]
  );

  // Admin access is enforced by the Security & Governance route.
  return (
    <div className="max-w-4xl">
      <p className="text-muted-foreground mb-4">
        Assign each user to a group, and set any filters that apply to them alone. Filters are automatically applied to all queries.
      </p>

      {isError ? (
        <div className="flex items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Failed to load users</p>
            <p className="text-sm text-muted-foreground">Something went wrong fetching the user list. Please try again.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 max-w-sm"
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Group</TableHead>
                <TableHead className="w-[80px] text-right">Settings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    {u.name || '-'}
                    {isAgentUser(u.id) && <Badge variant="outline" className="ml-2">Agent</Badge>}
                  </TableCell>
                  <TableCell>{u.email || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).map(r => (
                        <Badge key={r.id} variant="secondary">{r.name}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {memberships?.[u.id] ? (groupNameById[memberships[u.id]] ?? '(unknown group)') : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleOpenSettings(u)}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {searchQuery ? 'No users match your search.' : 'No users found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                {searchQuery && users ? ` (of ${users.length})` : ''}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedUser && isAgentUser(selectedUser.id) && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>Data permissions for {selectedUser.name}</DialogTitle>
              <DialogDescription>
                An agent: every step of it sees the data this allows. Its group's restrictions apply on top of its own.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="py-4 overflow-y-auto min-h-0">
              <PrincipalPermissions principalId={selectedUser.id} name={selectedUser.name} onDone={() => setDialogOpen(false)} />
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}
      {selectedUser && !isAgentUser(selectedUser.id) && (
        <UserPermissionsDialog
          user={selectedUser}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </div>
  );
}
